import { useState, useEffect } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { renderInputRow } from '../../../utils/renderInputRow';
import { FileUpload } from '../ui';

const SectionCard = ({ icon, title, desc, children }) => (
    <div className="cpiw-card">
        <div className="cpiw-card__header">
            <div className="cpiw-card__header-icon"><span style={{ fontSize: '16px' }}>{icon}</span></div>
            <div>
                <div className="cpiw-card__title">{title}</div>
                {desc && <div className="cpiw-card__desc">{desc}</div>}
            </div>
        </div>
        <div className="cpiw-card__body">{children}</div>
    </div>
);

const AddPincode = ({ editId = null, onEditDone = null, toast }) => {
    const emptyForm = { cpiwpincode: '', cpiwstate: '', cpiwcity: '', cpiwshipping: '', cpiwddate: '', cpiwcod: 'disable' };
    const [settings, setSettings] = useState(emptyForm);
    const [loading, setLoading]   = useState(false);
    const [file, setFile]         = useState(null);
    const pluginDir = window.CPIW_SETTINGS?.pluginDir || '';

    useEffect(() => {
        if (!editId) { setSettings(emptyForm); return; }
        setLoading(true);
        apiFetch({ path: `cpiw/v1/getpincode/${editId}?nocache=${Date.now()}` })
            .then((p) => {
                setSettings({
                    cpiwpincode:  p.pincode      || '',
                    cpiwstate:    p.state         || '',
                    cpiwcity:     p.city          || '',
                    cpiwshipping: p.ship_amount   || '',
                    cpiwddate:    p.ddate          || '',
                    cpiwcod:      p.caseondilvery == 1 ? 'enable' : 'disable',
                });
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [editId]);

    const handleSave = () => {
        if (!settings.cpiwpincode || !settings.cpiwcity || !settings.cpiwstate) {
            toast.error('Pincode, City, and State are required.');
            return;
        }
        setLoading(true);
        const request = editId
            ? { path: `cpiw/v1/updatepincode/${editId}`, method: 'PUT',  data: settings }
            : { path: `cpiw/v1/addpincode`, method: 'POST', data: settings };

        apiFetch(request)
            .then(() => {
                setLoading(false);
                if (editId) {
                    toast.success(`Pincode #${editId} updated successfully!`);
                    if (onEditDone) onEditDone();
                } else {
                    toast.success('Pincode added successfully!');
                    setSettings(emptyForm);
                }
            })
            .catch((err) => {
                setLoading(false);
                toast.error(editId ? 'Failed to update pincode.' : (err.message || 'Failed to add pincode.'));
            });
    };

    const handleUpload = () => {
        if (!file) { toast.warning('Please select a .csv file first.'); return; }
        const formData = new FormData();
        formData.append('file', file);
        setLoading(true);
        apiFetch({ path: `cpiw/v1/importcsv`, method: 'POST', body: formData })
            .then((res) => {
                setLoading(false);
                toast.success(`Imported ${res.inserted} records — ${res.skipped} skipped.`);
                setFile(null);
            })
            .catch((err) => { setLoading(false); toast.error(err.message || 'Import failed.'); });
    };

    const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

    return (
        <div className="plugin-add-pincode-page">
            {loading && (
                <div className="cpiw-overlay-loader">
                    <div className="cpiw-spinner-wrap">
                        <div className="cpiw-spinner" />
                        <span className="cpiw-spinner-label">{editId ? 'Updating…' : 'Saving…'}</span>
                    </div>
                </div>
            )}

            {editId && (
                <div className="cpiw-edit-banner">
                    <span>✎ Editing pincode <strong>#{editId}</strong></span>
                    {onEditDone && (
                        <button className="cpiw-btn-secondary cpiw-btn-sm" onClick={onEditDone}>
                            ← Back to List
                        </button>
                    )}
                </div>
            )}

            <SectionCard
                icon={editId ? '✎' : '➕'}
                title={editId ? `Edit Pincode #${editId}` : 'Add New Pincode'}
                desc={editId ? 'Update the details for this pincode entry.' : 'Fill in the details to add a single serviceable pincode.'}
            >
                {renderInputRow('Pincode',             'cpiwpincode',  '', [], '', settings, updateSetting)}
                {renderInputRow('State Name',          'cpiwstate',    '', [], '', settings, updateSetting)}
                {renderInputRow('City Name',           'cpiwcity',     '', [], '', settings, updateSetting)}
                {renderInputRow('Shipping Amount (₹)', 'cpiwshipping', 'number', [], '', settings, updateSetting)}
                {renderInputRow('Delivery Days',       'cpiwddate',    'number', [], '', settings, updateSetting)}
                {renderInputRow('Cash on Delivery',    'cpiwcod', 'checkBox', [], '', settings, updateSetting)}

                <div style={{ marginTop: '22px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button className="cpiw-btn" onClick={handleSave}>
                        {editId ? '✓ Update Pincode' : '+ Add Pincode'}
                    </button>
                    {editId && onEditDone && (
                        <button className="cpiw-btn-secondary" onClick={onEditDone}>Cancel</button>
                    )}
                </div>
            </SectionCard>

            {!editId && (
                <SectionCard icon="📦" title="Bulk Import via CSV" desc="Upload a CSV file to add many pincodes at once.">
                    <div className="cpiw-upload-row">
                        <div className="cpiw-field-label">
                            Import Pincode CSV <span className="pqd-pro-badge"><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 7l4 4 5-7 5 7 4-4-2 13H5L3 7z"/></svg>PRO</span>
                            <div className="cpiw-field-hint">Accepted format: .csv</div>
                        </div>
                        <div className="cpiw-upload-actions">
                            <FileUpload accept=".csv" onChange={(e) => setFile(e.target.files[0])} disabled={true}>
                                📂 Choose File
                            </FileUpload>
                            {file
                                ? <span className="cpiw-file-chosen">📄 {file.name}</span>
                                : <span className="cpiw-file-none">No file selected</span>
                            }
                            <button className="cpiw-btn-accent" style={{opacity: '0.5', pointerEvents: 'none'}}onClick={handleUpload}>↑ Import</button>
                            <span style={{fontSize: '13px'}}><a style={{color: '#6366f1', fontWeight: 600, fontSize: '13px', textDecoration: 'underline'}} href="https://www.plugin999.com/plugin/check-pincode-for-woocommerce/" target="_blank" rel="noopener noreferrer">Buy Pro Version</a> to import pincodes with CSV files.</span>
                        </div>
                    </div>

                    <div className="cpiw-upload-row">
                        <div className="cpiw-field-label">
                            Download Sample File
                            <div className="cpiw-field-hint">Use this as a template for your CSV.</div>
                        </div>
                        <div className="cpiw-upload-actions">
                            <a href={`${pluginDir}/sample.csv`} download="sample.csv" className="cpiw-btn-secondary">
                                ↓ Download sample.csv
                            </a>
                        </div>
                    </div>
                </SectionCard>
            )}
        </div>
    );
};

export default AddPincode;
